import React, {useCallback, useState, useRef, useEffect} from 'react';
import {api} from 'utils/api-client';
import {useQuery} from 'react-query';
import CompanyList from './components/company-list';
import CompanyForm from './components/company-form';
import {CompaniesResponse, Company} from '../../models/company';
import Header from '../../components/header';
import {useAuth} from '../../context/auth-context';
import Pagination from './components/pagination';
import styled from '@emotion/styled/macro';
import {ReactComponent as SearchIcon} from '@common/assets/search.svg';
import {ReactComponent as RedoIcon} from '@common/assets/redo.svg';
import {
  Input,
  IconButton,
} from 'components/form-items';
import qs from 'qs';
import useKeyPress from '@common/hooks/useKeyPress';
import {
  Button,
} from '@mayo/mayo-ui-gakki';

const Title = styled.span`
  font-size: 18px;
`;
const NumberText = styled.span`
  font-size: 18px;
  color: #26abe3;
`;

type CompaniesParams = {
  pageNo: number;
  pageSize: number;
  status: null | 0 | 1;
  vat?: string;
  companyName?: string;
}

function Index() {
  const {logout, user} = useAuth();
  const [isPressEnter, resetPressState] = useKeyPress('Enter')
  const [companiesParams, setCompaniesParams] = useState<CompaniesParams>({
    pageNo: 1,
    pageSize: 50,
    status: null,
  })
  const vatRef = useRef<any>()
  const companyNameRef = useRef<any>()
  const {data: companyInfo, isLoading, isFetching, refetch: refetchCompanyList} = useQuery(
    ['companies', companiesParams],
    (): Promise<CompaniesResponse> =>
      api.get(`backoffice?${qs.stringify(companiesParams)}`).then(data => data.data),
  );
  const {data: statisticInfo, isLoading: isStatisticLoading, refetch: refetchStatistic} = useQuery(
    'statistic',
    (): Promise<{
      nonStartup: number;
      onboarding: number;
      finished: number;
      total: number;
    }> => api.get(`backoffice/statistic`).then(data => data.data),
  );
  const [formInfo, setFormInfo] = useState<{
    type: 'create' | 'edit' | 'active' | 'hidden';
    isOpen: boolean;
    defaultValue?: Company;
  }>({
    type: 'hidden',
    isOpen: false,
    defaultValue: undefined,
  });

  const refetchPage = useCallback(() => {
    refetchCompanyList();
    refetchStatistic();
  }, [refetchCompanyList, refetchStatistic])
  const onEdit = useCallback(
    (data: Company) => {
      setFormInfo({
        type: 'edit',
        isOpen: true,
        defaultValue: data,
      });
    },
    [setFormInfo],
  );


  const onFilter = (status: null | 0 | 1) => {
    presetInput();
    setCompaniesParams({
      ...companiesParams,
      pageNo: 1,
      status,
    })
  }

  const onSearch = useCallback(() => {
    setCompaniesParams({
      ...companiesParams,
      pageNo: 1,
      vat: vatRef?.current?.value,
      companyName: companyNameRef?.current?.value,
    })
  }, [companiesParams])

  const clearInput = () => {
    if (vatRef?.current) vatRef.current.value = null;
    if (companyNameRef?.current) companyNameRef.current.value = null;
  }

  const presetInput = () => {
    if (vatRef?.current && companiesParams.vat) vatRef.current.value = companiesParams.vat;
    if (companyNameRef?.current && companiesParams.companyName) companyNameRef.current.value = companiesParams.companyName;
  }

  useEffect(() => {
    if (isPressEnter) {
      onSearch();
      resetPressState(false);
    }
  }, [isPressEnter, resetPressState, onSearch])

  const counts = [companyInfo?.disabledCount, companyInfo?.enabledCount, companyInfo?.totalCount];
  const countIndex = typeof companiesParams.status === 'number' ? companiesParams.status : 2;
  const currentCount = counts[countIndex] || 0;
  return (
    <>
      <Header username={user?.name || ''} onLogout={logout} />
      <div className={'p-4 grow bg-white overflow-auto'}>
        <div
          className={'grid h-full'}
          style={{
            gridTemplateRows: 'auto auto 1fr',
          }}
        >
          <div
            className={'mb-4 flex justify-between items-center'}
          >
            <div className={'flex gap-5'} id={'statistic-info'}>
              {!isStatisticLoading && (
                <>
                  <div>
                    <Title>專區總數：</Title>
                    <NumberText data-test-id={'total'}>
                      {statisticInfo?.total || 0}
                    </NumberText>
                  </div>
                  <div>
                    <Title>導入完成：</Title>
                    <NumberText data-test-id={'number-of-finished'}>
                      {statisticInfo?.finished || 0}
                    </NumberText>
                  </div>
                  <div>
                    <Title>尚未開始：</Title>
                    <NumberText data-test-id={'number-of-none-startup'}>
                      {statisticInfo?.nonStartup || 0}
                    </NumberText>
                  </div>
                  <div>
                    <Title>導入中：</Title>
                    <NumberText data-test-id={'number-of-onboarding'}>
                      {statisticInfo?.onboarding || 0}
                    </NumberText>
                  </div>
                </>
              )}
            </div>
            <Button
              variant={'solid'}
              colorSchema={'primary'}
              onClick={() => setFormInfo({isOpen: true, type: 'create'})}
              className={'add-company'}
              data-test-id={'add-new-company'}
            >
              新增專區
            </Button>
          </div>
          <div className="text-[13px] px-[15px] py-3 flex gap-y-0 flex-row justify-between items-center border-t-[1px] border-[#cfd5d8]">
            <div className="h-full w-auto flex items-center justify-around">
              <IconButton
                text={`全部：${companyInfo?.totalCount || '0'}`}
                active={companiesParams.status === null}
                onClick={() => onFilter(null)}
              />
              <div className="inline-block h-full w-[1px] bg-[#e9e9e9] mx-[15px]"/>
              <IconButton
                text={`已啟用：${companyInfo?.enabledCount || '0'}`}
                active={companiesParams.status === 1}
                onClick={() => onFilter(1)}
              />
              <div className="inline-block h-full w-[1px] bg-[#e9e9e9] mx-[15px]"/>
              <IconButton
                text={`未啟用：${companyInfo?.disabledCount || '0'}`}
                active={companiesParams.status === 0}
                onClick={() => onFilter(0)}
              />
            </div>
            <div className="h-full w-auto gap-y-0 flex flex-row items-center">
              <div className="h-full w-auto flex items-center">
                <label className="m-0 !mr-[15px]">統編</label>
                <Input
                  ref={vatRef}
                  className="!h-[25px] flex-1 !w-[165px] !rounded-full !bg-white"
                />
              </div>
              <div className="inline-block h-full w-[1px] bg-[#e9e9e9] mx-[15px]"/>
              <div className="h-full w-auto flex items-center">
                <label className="m-0 !mr-[15px]">公司名稱</label>
                <Input
                  ref={companyNameRef}
                  className="!h-[25px] flex-1 !w-[165px] !rounded-full !bg-white"
                />
              </div>
              <div className="h-full w-auto flex items-center justify-around">
                <div className="inline-block h-full w-[1px] bg-[#e9e9e9] mx-[15px]"/>
                <Button
                  variant={'simple'}
                  colorSchema={'primary'}
                  onClick={clearInput}
                  data-test-id={'reset-search'}
                  icon={<RedoIcon/>}
                >
                  清空
                </Button>
                <div className="inline-block h-full w-[1px] bg-[#e9e9e9] mx-[15px]"/>
                <Button
                  variant={'simple'}
                  colorSchema={'primary'}
                  onClick={onSearch}
                  data-test-id={'search'}
                  icon={<SearchIcon/>}
                >
                  搜尋
                </Button>
              </div>
            </div>
          </div>
          <div
            className={'border border-[#d4d9dc] overflow-auto max-w-[100vw] h-auto'}
          >
            <CompanyList
              companies={companyInfo?.companies || []}
              isLoading={isLoading || isFetching}
              onEdit={onEdit}
              refetchPage={refetchPage}
            />
          </div>
          <Pagination
            onPageChange={pageNo => {
              presetInput();
              setCompaniesParams({ ...companiesParams, pageNo })
            }}
            pageNumber={companiesParams.pageNo}
            pageCount={
              companyInfo
                ? Math.ceil(currentCount / companyInfo.offset) || 1
                : 1
            }
          />
          <CompanyForm
            isOpen={formInfo.isOpen}
            onClose={() =>
              setFormInfo({
                isOpen: false,
                type: 'hidden',
                defaultValue: undefined,
              })
            }
            defaultValue={formInfo.defaultValue}
            type={formInfo.type}
          />
        </div>
      </div>
    </>
  );
}

export default Index;
